import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { SlideOutTabProps } from './SlideOutTab.types';
import {
    SlideOutContainer,
    SlideOutWrapper,
    ContentContainer,
    StaticTab,
    PanelTab,
    Label,
    Overlay,
    BumpOut,
    MobileCloseButton,
    StaticTabContainer,
    BOUNCE_MS,
    RETURN_MS,
    PAUSE_MS,
} from './SlideOutTab.styles';
import { useVisibilityDetection } from './hooks/useVisibilityDetection';
import { useGlobal } from '../../hooks';
import {
    AnalyticsTargetLayerProvider,
    getPageUrl,
    useAnalyticsClickEvent,
} from 'rwl-ui';
import { ClickTargets, TargetLayer } from 'rwl-shared/dist/models/analytics';
import { useBreakpoint } from '../../providers/BreakpointProvider';
import { SlideOutContext, SlideOutControls } from './SlideOutContext';

/**
 * SlideOutTab Component
 *
 * A tab that can slide out from the side of the screen. It can be configured to
 * open from either the left or right side and positioned at the top, middle, or bottom.
 * It also supports custom styling options for the tab background and text.
 *
 * @param props The component props
 * @returns JSX.Element
 */
export const SlideOutTab = ({ children }: SlideOutTabProps): JSX.Element => {
    const { bumpOutTest, config } = useGlobal();
    const sendClickEvent = useAnalyticsClickEvent();
    const { isBelowBreakpoint } = useBreakpoint();
    const isMobile = isBelowBreakpoint('sm');

    const [intro, setIntro] = useState(false);

    useEffect(() => {
        const tStart = setTimeout(() => setIntro(true), 2000);
        const tStop = setTimeout(
            () => setIntro(false),
            2000 + BOUNCE_MS + PAUSE_MS + RETURN_MS
        );

        return () => {
            clearTimeout(tStart);
            clearTimeout(tStop);
        };
    }, []);

    if (!bumpOutTest) {
        return <>{children}</>;
    }
    const {
        openPanel,
        tabLocation,
        tabBackgroundColor,
        tabTextColor,
        ctaText,
        font,
        animateTab,
    } = bumpOutTest;

    // State to track if the panel is open or closed
    const [isOpen, setIsOpen] = useState(false);
    // State to track hover for StaticTab
    const [isStaticTabHovered, setIsStaticTabHovered] = useState(false);

    const openSlideOutPanel = useCallback(() => {
        setIsOpen((prev) => {
            if (prev) {
                return prev;
            }
            sendClickEvent({
                url: getPageUrl(),
                target: ClickTargets.BUMP_OUT_OPENED,
            });
            return true;
        });
    }, [sendClickEvent]);

    const closePanel = useCallback(() => {
        setIsOpen((prev) => {
            if (!prev) {
                return prev;
            }
            sendClickEvent({
                url: getPageUrl(),
                target: ClickTargets.BUMP_OUT_CLOSED,
            });
            return false;
        });
    }, [sendClickEvent]);

    const togglePanel = useCallback(() => {
        setIsOpen((prev) => {
            const next = !prev;
            sendClickEvent({
                url: getPageUrl(),
                target: next
                    ? ClickTargets.BUMP_OUT_OPENED
                    : ClickTargets.BUMP_OUT_CLOSED,
            });
            return next;
        });
    }, [sendClickEvent]);

    const controls = useMemo<SlideOutControls>(
        () => ({
            open: openSlideOutPanel,
            close: closePanel,
            toggle: togglePanel,
            isOpen,
        }),
        [closePanel, openSlideOutPanel, togglePanel, isOpen]
    );

    // Close panel when clicking outside
    const handleOverlayClick = useCallback(() => {
        closePanel();
    }, [closePanel]);

    // Use our custom hook to handle visibility detection
    useVisibilityDetection(isOpen);

    return (
        <SlideOutContext.Provider value={controls}>
            <AnalyticsTargetLayerProvider targetLayer={TargetLayer.BUMP_OUT}>
                {/* Darkened overlay when panel is open */}
                <Overlay
                    isOpen={isOpen}
                    onClick={handleOverlayClick}
                    data-testid="slide-out-overlay"
                />

                {/* BumpOut and StaticTab - visible when panel is closed */}
                <StaticTabContainer
                    className={[
                        isStaticTabHovered && 'static-tab-hovered',
                        intro && animateTab && 'static-tab-intro',
                    ]
                        .filter((c): c is string => Boolean(c))
                        .join(' ')}
                    openPanel={openPanel}
                    isStaticTabHovered={isStaticTabHovered}
                >
                    <BumpOut
                        publicUrl={config?.publicUrl}
                        openPanel={openPanel}
                        bumpOutColor={tabBackgroundColor}
                    />
                    <StaticTab
                        onClick={togglePanel}
                        data-testid="static-tab-handle"
                        isOpen={isOpen}
                        tabBackgroundColor={tabBackgroundColor}
                        openPanel={openPanel}
                        tabLocation={tabLocation}
                        onMouseEnter={() => setIsStaticTabHovered(true)}
                        onMouseLeave={() => setIsStaticTabHovered(false)}
                    >
                        <Label
                            openPanel={openPanel}
                            tabTextColor={tabTextColor}
                            font={font}
                        >
                            {ctaText || 'See work near you'}
                        </Label>
                    </StaticTab>
                </StaticTabContainer>

                {/* Mobile close button - only shows when panel is open on mobile */}
                {isMobile && isOpen && (
                    <MobileCloseButton
                        onClick={togglePanel}
                        tabBackgroundColor={tabBackgroundColor}
                        tabTextColor={tabTextColor}
                        data-testid="mobile-close-button"
                    />
                )}

                <SlideOutContainer>
                    <SlideOutWrapper
                        isOpen={isOpen}
                        data-testid="slide-out-wrapper"
                        tabBackgroundColor={tabBackgroundColor}
                        openPanel={openPanel}
                        isMobile={isMobile}
                    >
                        <ContentContainer>{children}</ContentContainer>

                        {/* Panel tab - visible when panel is open - hidden on mobile */}
                        {!isMobile && (
                            <PanelTab
                                onClick={togglePanel}
                                data-testid="panel-tab-handle"
                                isOpen={isOpen}
                                tabBackgroundColor={tabBackgroundColor}
                                openPanel={openPanel}
                                tabLocation={tabLocation}
                            >
                                <Label
                                    openPanel={openPanel}
                                    tabTextColor={tabTextColor}
                                    font={font}
                                >
                                    {ctaText || 'See work near you'}
                                </Label>
                            </PanelTab>
                        )}
                    </SlideOutWrapper>
                </SlideOutContainer>
            </AnalyticsTargetLayerProvider>
        </SlideOutContext.Provider>
    );
};
